import React, { useMemo } from 'react';
import { nafColor } from '@nafcore/theme';
import { Bolt } from '@styled-icons/material/Bolt';
import { DirectionsCar } from '@styled-icons/material/DirectionsCar';
import { WbSunny, Cloud, CloudQueue, AcUnit } from '@styled-icons/material-outlined';
import { Link } from 'react-router-dom';
import { TooltipIcon } from '@naf-simba/elements-tooltips';
import {
  ScrollContainer,
  TableContainer,
  Th,
  StickyTh,
  ModelColumn,
  StyledHeaderText,
  Tr,
  Td,
  Cell,
  ModelSubtitle,
  TooltipWrapper,
} from './Styles';
import RoadIcon from '../../../icons/Road';
import { Rain } from '../../../icons/Rain';
import { TestedVehicleVariantSelect } from '../VariantSelect/VariantSelect';
import useSelector from '../../../../redux/typedHooks';
import { TestedVehicle } from '../../../../../../types/TestedVehicle';
import { getVariantId, getSeasonName } from '../utils';
import { Spinner } from '../../../spinner/Spinner';

interface TableProps {
  data: TestedVehicle[];
  allSeasons?: boolean;
}

const WeatherIcons = (weatherType: string) => {
  switch (weatherType) {
    case 'clearsky_day':
      return WbSunny;
    case 'clearsky_night':
      return WbSunny;
    case 'cloudy_day':
      return CloudQueue;
    case 'cloudy_night':
      return CloudQueue;
    case 'overcast':
      return Cloud;
    case 'downpour_rain':
      return Rain;
    case 'downpour_snow':
      return AcUnit;
    default:
      return '–';
  }
};

const getWeatherPriority = (weatherType: string): number => {
  switch (weatherType) {
    case 'clearsky_day':
    case 'clearsky_night':
      return 1;
    case 'cloudy_day':
    case 'cloudy_night':
      return 2;
    case 'overcast':
      return 3;
    case 'downpour_rain':
      return 4;
    case 'downpour_snow':
      return 5;
    default:
      return 0;
  }
};

const show = (item: string | number | null | undefined, unit?: string) => {
  if (item === null || item === undefined || item === 0) return <>&#8211;</>;
  if (unit) return `${item} ${unit}`;
  return item;
};

const percentageDiff = (given: number | null, measured: number | null) => {
  if (!given || !measured) {
    return null;
  }
  const percent = ((given - measured) / given) * 100;
  return -Math.round((percent + Number.EPSILON) * 100) / 100;
};

const temperatureRange = (
  lowestTemperature: number | null | undefined,
  highestTemperature: number | null | undefined,
) => {
  if (!lowestTemperature && !highestTemperature) return '–';
  if (lowestTemperature === highestTemperature) return `${lowestTemperature}°C`;
  if (lowestTemperature === null) return `${highestTemperature}°C`;
  if (highestTemperature === null) return `${lowestTemperature}°C`;

  return `${lowestTemperature}°C til ${highestTemperature}°C`;
};

const TableRow = React.memo(({ row, iconColor }: { row: TestedVehicle; iconColor: string }) => {
  const {
    Slug,
    Brand,
    Model,
    LowestTemperature,
    HighestTemperature,
    Conditions,
    Name,
    Year,
    TestedYear,
    TestedSeason,
  } = row ?? {};
  const { StatedRangeKm, MeasuredRangeKm } = row?.RangeTest?.[0] ?? {};
  const { StatedTimeTo80PercentMinutes, MeasuredTimeTo80PercentMinutes } = row?.ChargeTest?.[0] ?? {};

  const rangeDiff = percentageDiff(StatedRangeKm, MeasuredRangeKm);
  const chargeDiff = percentageDiff(StatedTimeTo80PercentMinutes, MeasuredTimeTo80PercentMinutes);

  const weatherIcons = useMemo(() => {
    if (!Conditions?.length) return '–';

    const sortedConditions = [...Conditions].sort((a, b) => getWeatherPriority(b) - getWeatherPriority(a));
    const worstAndBestConditions = [...new Set([sortedConditions[0], sortedConditions[sortedConditions.length - 1]])];

    return worstAndBestConditions.map((condition, index) => {
      const Icon = WeatherIcons(condition);
      if (Icon === '–') return null;

      return (
        <div key={condition}>
          <Icon fill={iconColor} size={24} />
          {index === 0 && worstAndBestConditions.length > 1 && ' / '}
        </div>
      );
    });
  }, [Conditions, iconColor]);

  const displayName = Name || '';
  const displayYear = parseInt(Year.toString(), 10) > 1900 ? Year : '-';

  return (
    <Tr key={getVariantId(row)}>
      <ModelColumn $right>
        <Link to={`/bilguiden/bilmodell/${Slug}`}>
          {Brand} {Model}
        </Link>
        <ModelSubtitle>{`${displayName || ''} ${displayYear}`}</ModelSubtitle>
        <ModelSubtitle>{`Testet ${getSeasonName(TestedSeason)} ${TestedYear}`}</ModelSubtitle>
      </ModelColumn>
      <Td>
        <Cell>{show(StatedRangeKm, 'km')}</Cell>
        <Cell $center>{show(MeasuredRangeKm, 'km')}</Cell>
        <Cell $value={rangeDiff} $right>
          {show(rangeDiff ? `${rangeDiff}%` : null)}
        </Cell>
      </Td>
      <Td>
        <Cell>{show(StatedTimeTo80PercentMinutes, 'min')}</Cell>
        <Cell $center>{show(MeasuredTimeTo80PercentMinutes, 'min')}</Cell>
        <Cell $negativeValue={chargeDiff}>{show(chargeDiff ? `${chargeDiff}%` : null)}</Cell>
      </Td>
      <Td>
        <Cell $center $padding={10}>
          {temperatureRange(LowestTemperature, HighestTemperature)}
        </Cell>
        <Cell $right>{weatherIcons}</Cell>
      </Td>
    </Tr>
  );
});

export const Table = ({ data, allSeasons = true }: TableProps) => {
  const iconColor = nafColor.neutral.neutral6;
  const selectedVariants = useSelector((state) => state.carFilter.selectedVariants ?? []);

  const sortedVariants = useMemo(
    () =>
      data
        ?.filter((variant) => selectedVariants?.includes(getVariantId(variant)))
        .sort(
          (a, b) =>
            (selectedVariants?.indexOf(getVariantId(a)) ?? 0) - (selectedVariants?.indexOf(getVariantId(b)) ?? 0),
        ),
    [data, selectedVariants],
  );

  const tooltipText =
    'Temperatur og værforhold vil spille inn på blant annet rekkevidden. Det er derfor viktig at du er klar over hvordan været var da testene ble gjennomført';

  if (!data) {
    return <Spinner />;
  }

  return (
    <>
      <TestedVehicleVariantSelect variants={data} allSeasons={allSeasons} />
      {sortedVariants?.length > 0 ? (
        <ScrollContainer>
          <TableContainer>
            <Tr $hideBorder>
              <StickyTh $width={180} $right />
              <Th $right>
                <RoadIcon fill={iconColor} size={24} />
                <StyledHeaderText>Rekkevidde (km)</StyledHeaderText>
              </Th>
              <Th>
                <Bolt fill={iconColor} size={24} />
                <StyledHeaderText>Ladetid (10 - 80%)</StyledHeaderText>
              </Th>
              <Th $width={206} $left $right>
                <WbSunny fill={iconColor} size={24} />
                <StyledHeaderText>Været</StyledHeaderText>
                <TooltipWrapper>
                  <TooltipIcon title={tooltipText} />
                </TooltipWrapper>
              </Th>
            </Tr>
            <Tr>
              <StickyTh $alignLeft $right>
                <DirectionsCar fill={iconColor} size={24} />
                <StyledHeaderText>Bilmodell</StyledHeaderText>
              </StickyTh>
              <Td>
                <Cell>WLTP</Cell>
                <Cell>Målt</Cell>
                <Cell $right>Avvik</Cell>
              </Td>
              <Td>
                <Cell>Oppgitt</Cell>
                <Cell>Målt</Cell>
                <Cell>Avvik</Cell>
              </Td>
              <Td>
                <Cell $left>Temp.</Cell>
                <Cell $right>Forhold</Cell>
              </Td>
            </Tr>
            {(sortedVariants ?? []).map((row) => (
              <TableRow key={getVariantId(row)} row={row} iconColor={iconColor} />
            ))}
          </TableContainer>
        </ScrollContainer>
      ) : null}
    </>
  );
};
